// components/sections/TermSection.js

import React from 'react';
import InteractiveField from '../common/InteractiveField';

/**
 * Компонент для раздела "2. Срок действия, регистрация и расторжение Договора".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 */
const TermSection = ({ data, onChange }) => {
  const {
    durationMonths = 11,
    termSelection = 'less_than_year',
    registrationResponsible = 'landlord',
    registrationCostBearer = 'landlord'
  } = data;

  // Определяем, нужна ли регистрация (срок 12 месяцев или более)
  const isRegistrationRequired = durationMonths >= 12;

  // Обработчик для главных кнопок выбора срока ("Менее года" / "Более года")
  const handleTermSelectionChange = (selection) => {
    if (selection === 'less_than_year') {
      // При выборе "Менее года" ставим 11 месяцев по умолчанию
      onChange({ ...data, termSelection: 'less_than_year', durationMonths: 11 });
    } else {
      // При выборе "Более года" ставим 12 месяцев по умолчанию
      onChange({ ...data, termSelection: 'more_than_year', durationMonths: 12 });
    }
  };

  // Обработчик для ручного изменения количества месяцев в поле ввода
  const handleDurationChange = (value) => {
    const months = parseInt(value, 10) || 0;
    // Автоматически переключаем состояние кнопок в зависимости от введенного числа
    const newSelection = months >= 12 ? 'more_than_year' : 'less_than_year';
    onChange({ ...data, durationMonths: months, termSelection: newSelection });
  };
  
  // Обработчик для выбора ответственных за регистрацию
  const handleRegistrationChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  // Динамический счетчик пунктов для корректной нумерации
  let clauseCounter = 1;

  return (
    <div className="document-preview-editor">
      <h2 className="special-section-title" style={{ width: '100%', display: 'block', textAlign: 'center', marginBottom: '24px' }}>
        {isRegistrationRequired 
          ? "2. Срок действия, порядок регистрации, изменения и расторжения Договора"
          : "2. Срок действия, порядок изменения и расторжения Договора"}
      </h2>

      {/* ✨ ИЗМЕНЕНИЕ: Единый блок для связанных пунктов */}
      <div className="dynamic-content-block active-purple">
        <p className="document-preamble">
          <b>2.{clauseCounter}.</b> Срок найма составляет{' '}
          <InteractiveField
            value={durationMonths}
            onChange={handleDurationChange}
            placeholder="11"
            type="number"
            isRequired={true}
          />{' '}
          месяцев с момента передачи Нанимателю Помещения.
        </p>

        {/* ✨ ИЗМЕНЕНИЕ: Блок кнопок перенесен под пункт 2.1 */}
        <div className="option-pills-group" style={{ justifyContent: 'flex-start', marginTop: '16px', marginBottom: '16px' }}>
          <button
            className={`option-pill ${termSelection === 'less_than_year' ? 'selected' : ''}`}
            onClick={() => handleTermSelectionChange('less_than_year')}
          >
            Менее одного года
          </button>
          <button
            className={`option-pill ${termSelection === 'more_than_year' ? 'selected' : ''}`}
            onClick={() => handleTermSelectionChange('more_than_year')}
          >
            Год и более
          </button>
        </div>

        {/* Блок с условиями регистрации, который появляется только при сроке от 12 месяцев */}
        {isRegistrationRequired && (
          <div className="configurable-section">
            <p className="document-preamble" style={{ fontStyle: 'italic', color: 'var(--text-secondary)', background: 'var(--bg-subtle)', padding: '8px', borderRadius: '8px' }}>
              *Так как срок найма составляет 12 месяцев или более, Договор подлежит обязательной государственной регистрации.
            </p>

            <p className="document-preamble">
              <b>2.{++clauseCounter}.</b> Государственную регистрацию найма помещения в течение месяца со дня заключения настоящего Договора производит: <b>{registrationResponsible === 'landlord' ? 'Наймодатель' : 'Наниматель'}</b>.
            </p>
            <div className="option-pills-group" style={{ justifyContent: 'flex-start', marginTop: '16px', marginBottom: '16px' }}>
                <button
                  className={`option-pill ${registrationResponsible === 'landlord' ? 'selected' : ''}`}
                  onClick={() => handleRegistrationChange('registrationResponsible', 'landlord')}
                >
                  Наймодатель
                </button>
                <button
                  className={`option-pill ${registrationResponsible === 'tenant' ? 'selected' : ''}`}
                  onClick={() => handleRegistrationChange('registrationResponsible', 'tenant')}
                >
                  Наниматель
                </button>
            </div>
            
            <p className="document-preamble">
              Расходы по государственной регистрации возлагаются на <b>{registrationCostBearer === 'landlord' ? 'Наймодателя' : 'Нанимателя'}</b>.
            </p>
            <div className="option-pills-group" style={{ justifyContent: 'flex-start', marginTop: '16px', marginBottom: '16px' }}>
                 <button
                  className={`option-pill ${registrationCostBearer === 'landlord' ? 'selected' : ''}`}
                  onClick={() => handleRegistrationChange('registrationCostBearer', 'landlord')}
                >
                  Наймодателя
                </button>
                <button
                  className={`option-pill ${registrationCostBearer === 'tenant' ? 'selected' : ''}`}
                  onClick={() => handleRegistrationChange('registrationCostBearer', 'tenant')}
                >
                  Нанимателя
                </button>
            </div>

            <p className="document-preamble">
              <b>2.{++clauseCounter}.</b> Ограничение права собственности на Помещение, возникающее на основании настоящего Договора, заключенного на срок не менее года, подлежит государственной регистрации в порядке, установленном Федеральным законом от 13.07.2015 N 218-ФЗ "О государственной регистрации недвижимости".
            </p>
          </div>
        )}
      </div>

      {/* Пункт о продлении договора — разный для долго- и краткосрочного договора */}
      {isRegistrationRequired ? (
        <p className="document-preamble">
          <b>2.{++clauseCounter}.</b> По истечении срока настоящего Договора Наниматель имеет преимущественное право на заключение Договора на новый срок. Не позднее чем за три месяца до истечения срока Договора Наймодатель должен предложить Нанимателю заключить Договор на тех же или иных условиях либо предупредить Нанимателя об отказе от продления Договора в связи с решением не сдавать в течение не менее года жилое помещение внаем. Если Наймодатель не выполнил этой обязанности, а Наниматель не отказался от продления Договора, Договор считается продленным на тех же условиях и на тот же срок.
        </p>
      ) : (
        <p className="document-preamble">
          <b>2.{++clauseCounter}.</b> Если ни одна из Сторон не заявит о своем намерении прекратить Договор не позднее чем за 30 (тридцать) календарных дней до окончания срока его действия, Договор считается продленным на тех же условиях на новый срок — {durationMonths} месяцев.
        </p>
      )}

      {/* Пункт о расторжении по требованию Наймодателя — разный для долго- и краткосрочного договора */}
      {isRegistrationRequired ? (
        <p className="document-preamble">
          <b>2.{++clauseCounter}.</b> Настоящий Договор может быть расторгнут в судебном порядке по требованию Наймодателя в случаях:
          <br/>- невнесения Нанимателем платы за Помещение в течение шести месяцев подряд;
          <br/>- разрушения или порчи Помещения Нанимателем или лицами, за которых он отвечает (в том числе вселения в Помещение животных, курения в Помещении, если это запрещено настоящим Договором);
          <br/>- использования Помещения не по назначению (в том числе сдача в поднаем без согласия Наймодателя, осуществление предпринимательской деятельности.);
          <br/>- систематического (два и более раза) нарушения прав и интересов соседей.
        </p>
      ) : (
        <p className="document-preamble">
          <b>2.{++clauseCounter}.</b> Настоящий Договор может быть расторгнут в судебном порядке по требованию Наймодателя в случаях:
          <br/>- невнесения Нанимателем платы за Помещение более двух раз по истечении установленного Договором срока платежа;
          <br/>- разрушения или порчи Помещения Нанимателем или лицами, за которых он отвечает (в том числе вселения в Помещение животных, курения в Помещении, если это запрещено настоящим Договором);
          <br/>- использования Помещения не по назначению (в том числе сдача в поднаем без согласия Наймодателя, осуществление предпринимательской деятельности.);
          <br/>- систематического (два и более раза) нарушения прав и интересов соседей.
        </p>
      )}
      <p className="document-preamble">
        <b>2.{++clauseCounter}.</b> Настоящий Договор может быть расторгнут в судебном порядке по требованию любой из Сторон:
        <br/>- если Помещение перестает быть пригодным для постоянного проживания, а также в случае его аварийного состояния.
        <br/>- в других случаях, предусмотренных жилищным законодательством.
      </p>
      <p className="document-preamble">
        <b>2.{++clauseCounter}.</b> Наниматель вправе с согласия других граждан, постоянно проживающих с ним, в любое время расторгнуть настоящий Договор с письменным предупреждением Наймодателя за три месяца.
      </p>
    </div>
  );
};

export default TermSection;
